// Modal edycji jednostki pojedynczej pozycji oferty handlowca.
// Backend zostawia wszystkie pozostale pola bez zmian - przeliczamy oferte na nowej jednostce.

import React, { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, MenuItem, TextField } from '@mui/material';

import { WiseModal } from 'components/wise';
import { useGetClientProducts, usePutSalesCrmOfferPosition } from 'api';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/EditOfferPositionUnitModal.module.scss';

interface IPositionInput {
  id: number;
  productId: number;
  productUnitId: number | null;
  name: string;
  symbol: string;
  imageUrl: string | null;
  unit: string;
}

interface IEditOfferPositionUnitModalProps {
  open: boolean;
  clientId: number;
  offerId: string;
  position: IPositionInput;
  onClose: () => void;
  onUpdated?: () => void;
}

const EditOfferPositionUnitModal: FC<IEditOfferPositionUnitModalProps> = ({
  open,
  clientId,
  offerId,
  position,
  onClose,
  onUpdated
}) => {
  const { t } = useTranslation();
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(position.productUnitId);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);

  // Re-sync przy zmianie pozycji (przelaczenie miedzy wierszami bez unmount).
  useEffect(() => {
    setSelectedUnitId(position.productUnitId);
    setErrorMessage(null);
    setImageFailed(false);
  }, [position.id, position.productUnitId]);

  // Wyszukujemy produkt przez listingowy endpoint klienta (zawiera `product_unit_short_info.units[].id`
  // czyli ProductUnit.id - to ID jest wymagane przez backend w polu `productUnitId` payloadu PUT).
  // Endpoint `/products/{id}` zwraca `prices[].id` z innej puli ID i nie matchuje pozycji.
  const searchKeyword = position.symbol || position.name;
  const { data: searchResult, isFetching: isProductLoading } = useGetClientProducts(
    clientId,
    searchKeyword,
    { enabled: open && position.productId > 0 && searchKeyword.length > 0 }
  );
  const product = searchResult?.items?.find((p) => p.id === position.productId) ?? null;

  const { mutate: updatePosition, isLoading: isUpdating } = usePutSalesCrmOfferPosition(
    clientId,
    offerId,
    {
      onSuccess: async () => {
        await onUpdated?.();
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error?.message || t('Nie udalo sie zapisac zmian.'));
      }
    }
  );

  const availableUnits = product?.product_unit_short_info?.units ?? [];
  const defaultUnitDictId = product?.product_unit_short_info?.default_unit_id ?? null;

  const handleSubmit = () => {
    if (selectedUnitId === null) {
      setErrorMessage(t('Wybierz jednostke.'));
      return;
    }
    if (selectedUnitId === position.productUnitId) {
      onClose();
      return;
    }
    setErrorMessage(null);
    updatePosition({
      positionId: position.id,
      payload: { productUnitId: selectedUnitId }
    });
  };

  return (
    <WiseModal open={open} onClose={onClose} title={t('Zmien jednostke pozycji')} maxWidth={520}>
      <div className={styles.body}>
        <div className={styles.selectedHeader}>
          <div className={styles.selectedImageWrapper}>
            {position.imageUrl && !imageFailed ? (
              <img
                src={position.imageUrl}
                alt={position.name}
                className={styles.selectedImage}
                onError={() => setImageFailed(true)}
              />
            ) : (
              <div className={styles.selectedImagePlaceholder} aria-label={t('Brak grafiki')}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
              </div>
            )}
          </div>
          <div className={styles.selectedInfo}>
            <div className={styles.selectedName}>{position.name}</div>
            {position.symbol && (
              <div className={styles.selectedSymbol}>{position.symbol}</div>
            )}
          </div>
        </div>

        <TextField
          className={styles.unitField}
          label={t('Jednostka')}
          select
          value={selectedUnitId ?? ''}
          onChange={(e) =>
            setSelectedUnitId(e.target.value === '' ? null : Number(e.target.value))
          }
          size="small"
          disabled={isProductLoading || availableUnits.length === 0}
          fullWidth>
          {availableUnits.length === 0 && (
            <MenuItem value="" disabled>
              {isProductLoading ? t('Ladowanie...') : t('Brak jednostek')}
            </MenuItem>
          )}
          {availableUnits.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.name}
              {u.unit_id === defaultUnitDictId ? ` (${t('domyslna')})` : ''}
            </MenuItem>
          ))}
        </TextField>

        {errorMessage && <div className={styles.error}>{errorMessage}</div>}

        <div className={styles.actions}>
          <Button onClick={onClose} variant="text">
            {t('Anuluj')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isUpdating || selectedUnitId === null}
            sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
            {isUpdating ? <CircularProgress size={18} /> : t('Zapisz')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default EditOfferPositionUnitModal;
